import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { Button } from "../../../../components/Button";
import { FontSize } from "../../../../components/Text";
import { TestIDs } from "../../../../tests/types/testIds";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { EntityIcons } from "../../../../utils/GlobalIcons";
import {
  SetupCard,
  SetupCardActions,
  SetupCardDescription,
  SetupCardTitle,
} from "./DashboardSetupCard";
import { DashboardSetupCardLabel } from "./DashboardSetupCardLabel";

const Lanes = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${Spacing.xs};
`;

const Lane = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs};
  border-radius: ${Rounded.md};
  background-color: ${({ theme }) => theme.sub};
`;

const LaneName = styled.span`
  font-size: ${FontSize.xs};
  font-weight: 500;
  color: ${({ theme }) => theme.darkerSub};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GhostCard = styled.span<{ $first?: boolean }>`
  height: 1.75rem;
  border-radius: ${Rounded.sm};
  border: 1.5px dashed
    ${({ theme, $first }) => ($first ? theme.main : theme.darkSub)};
  background-color: ${({ theme, $first }) =>
    $first ? theme.white : theme.transparent};
`;

type DashboardSetupDealCardProps = {
  featured?: boolean;
  done?: boolean;
};

export const DashboardSetupDealCard: React.FC<DashboardSetupDealCardProps> = ({
  featured,
  done,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <SetupCard
      $featured={featured}
      $done={done}
      data-testid={TestIDs.dashboard.setup.card("DEAL")}
    >
      <DashboardSetupCardLabel featured={featured} done={done} />
      <SetupCardTitle $featured={featured} $done={done}>
        {t("dashboard.setup.deal.title")}
      </SetupCardTitle>
      <SetupCardDescription $done={done}>
        {t("dashboard.setup.deal.description")}
      </SetupCardDescription>

      <Lanes aria-hidden>
        <Lane>
          <LaneName>{t("dashboard.setup.deal.lane_new")}</LaneName>
          <GhostCard $first />
          <GhostCard />
        </Lane>
        <Lane>
          <LaneName>{t("dashboard.setup.deal.lane_proposal")}</LaneName>
          <GhostCard />
        </Lane>
        <Lane>
          <LaneName>{t("dashboard.setup.deal.lane_won")}</LaneName>
          <GhostCard />
        </Lane>
      </Lanes>

      <SetupCardActions>
        <Button
          icon={EntityIcons.Deals}
          onClick={() => navigate("/deals")}
          dataTestId={TestIDs.dashboard.setup.cta("DEAL")}
        >
          {t("dashboard.setup.deal.cta")}
        </Button>
      </SetupCardActions>
    </SetupCard>
  );
};
